/**
 * Bottom-sheet form for creating and editing posts, events, and offers.
 * Managed via PostFormContext — consume openNew/openEdit/close rather than mounting directly.
 */
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { X, Loader2, LifeBuoy, Users } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { POST_CHAR_LIMIT, LANGUAGES } from '@olimhub/shared'
import type { Language, HelpPost } from '@olimhub/shared'
import { postsApi } from '@/api'
import type { AnyPost } from '@/api/endpoints/posts'
import { useToast } from '@/components/common/Toast'
import { useFeedStore } from '@/store/feed.store'
import { ImageUpload } from './ImageUpload'
import { useMentionAutocomplete } from '@/hooks/useMentionAutocomplete'
import { useIsDesktop, useCategoryOptions } from '@/hooks'
import { MentionDropdown } from './MentionDropdown'
import type { PostFormGroupContext } from '@/context/PostFormContext'

const UI_LANGUAGES = LANGUAGES.filter((l) => l !== 'he')

function resolveUiLang(lang: string): Language {
  const code = lang.slice(0, 2)
  return (UI_LANGUAGES as readonly string[]).includes(code) ? (code as Language) : 'en'
}

interface PostFormProps {
  open: boolean
  onClose: () => void
  initialPost?: AnyPost
  initialTab?: 'post' | 'event' | 'bazaar' | 'help' | 'action'
  groupContext?: PostFormGroupContext
}

type PostTypeTab = 'post' | 'event' | 'bazaar' | 'help' | 'action'

export function PostForm({ open, onClose, initialPost, initialTab, groupContext }: PostFormProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { triggerRefresh } = useFeedStore()
  const { toast } = useToast()
  const { t, i18n } = useTranslation()
  const isDesktop = useIsDesktop()

  const [type, setType] = useState<PostTypeTab>('post')
  const [content, setContent] = useState('')
  const [language, setLanguage] = useState<Language>(resolveUiLang(i18n.language))
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const { options: categoryOptions, language: categoryLang } = useCategoryOptions('help', type === 'help')
  const [isUrgent, setIsUrgent] = useState(false)
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [crossPost, setCrossPost] = useState(false)
  // Unified group-post entry (SPEC_group_post_creation_unified_entry.md):
  // identity is chosen inside the composer, not by which button opened it.
  // Defaults to posting as yourself — "as the group" is an explicit opt-in,
  // only offered when groupContext.canPostAsGroup is true.
  const [postAsGroup, setPostAsGroup] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const mention = useMentionAutocomplete(content, setContent, textareaRef)

  const isPostingAsGroup = !!groupContext?.canPostAsGroup && postAsGroup

  // Pre-fill fields when editing, reset for new post. Event/offer/action
  // editing routes away to their own full forms (PostFormContext.openEdit) —
  // this modal only ever edits 'post' or 'help'.
  useEffect(() => {
    if (initialPost) {
      setContent(initialPost.content)
      setLanguage(resolveUiLang(initialPost.language))
      setImageUrl(initialPost.imageUrl ?? null)
      setCrossPost(initialPost.showInLocalFeed)
      setCategoryId(initialPost.category?.id ?? null)
      if (initialPost.type === 'help') {
        const helpPost = initialPost as HelpPost
        setType('help')
        setIsUrgent(helpPost.help.isUrgent)
      } else {
        setType('post')
      }
    } else {
      setContent('')
      setLanguage(resolveUiLang(i18n.language))
      setImageUrl(null)
      setType(initialTab ?? 'post')
      setCategoryId(null)
      setIsUrgent(false)
      setSubmitAttempted(false)
      setCrossPost(false)
      setPostAsGroup(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPost, open, initialTab, groupContext])

  const remaining = POST_CHAR_LIMIT - content.length
  const isOverLimit = remaining < 0
  const isContentEmpty = content.trim().length === 0

  const hasContent = type === 'help' ? !isContentEmpty : (!isContentEmpty || !!imageUrl)
  const isSubmitValid = hasContent && !isOverLimit
  const isContinueValid = hasContent && !isOverLimit

  const handleSubmit = async () => {
    if (isSubmitting) return
    if (!isSubmitValid) {
      setSubmitAttempted(true)
      return
    }
    setIsSubmitting(true)
    try {
      if (initialPost) {
        const showCrossPostOnEdit = initialPost.account.accountType === 'group' || !!initialPost.groupTag
        await postsApi.update(initialPost.id, {
          content,
          language,
          imageUrl: imageUrl ?? undefined,
          ...(type === 'help' ? { isUrgent } : {}),
          categoryId,
          showInLocalFeed: showCrossPostOnEdit ? crossPost : undefined,
        })
        await queryClient.invalidateQueries({ queryKey: ['feed'] })
        await queryClient.invalidateQueries({ queryKey: ['group-feed'] })
        await queryClient.invalidateQueries({ queryKey: ['post', initialPost.id] })
        onClose()
        toast.success('Post updated!')
      } else if (type === 'help') {
        await postsApi.create({
          type: 'help',
          content,
          language,
          categoryId,
          isUrgent,
          postAsGroupSlug: isPostingAsGroup ? groupContext!.slug : undefined,
          groupTagSlug: groupContext && !isPostingAsGroup ? groupContext.slug : undefined,
          showInLocalFeed: groupContext ? crossPost : undefined,
        })
        onClose()
        if (groupContext) await queryClient.invalidateQueries({ queryKey: ['group-feed', groupContext.slug] })
        if (!groupContext || crossPost) triggerRefresh()
        toast.success(t('help.published'))
      } else {
        await postsApi.create({
          type: 'post',
          content,
          language,
          imageUrl: imageUrl ?? undefined,
          postAsGroupSlug: isPostingAsGroup ? groupContext!.slug : undefined,
          groupTagSlug: groupContext && !isPostingAsGroup ? groupContext.slug : undefined,
          showInLocalFeed: groupContext ? crossPost : undefined,
        })
        onClose()
        if (groupContext) await queryClient.invalidateQueries({ queryKey: ['group-feed', groupContext.slug] })
        if (!groupContext || crossPost) triggerRefresh()
        toast.success(groupContext ? t('groups.post_published') : 'Post shared!')
      }
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleContinue = () => {
    if (!isContinueValid) {
      setSubmitAttempted(true)
      return
    }
    if (type === 'event') {
      navigate('/posts/new/event', { state: { content, language, imageUrl, groupContext, crossPost, postAsGroup } })
      onClose()
    } else if (type === 'bazaar') {
      navigate('/posts/new/bazaar', { state: { content, language, imageUrl, groupContext, crossPost, postAsGroup } })
      onClose()
    } else if (type === 'action') {
      navigate('/posts/new/action', { state: { content, language, imageUrl, groupContext, crossPost, postAsGroup } })
      onClose()
    }
  }

  // Action is only offered inside a group, to the same admin/editor roles
  // who can post as the group (post_as_group and manage_actions are held
  // by the same two badges — see spec-action-eot-mvp.md).
  const tabs: Array<{ key: PostTypeTab; label: string }> = [
    { key: 'post', label: t('post.post_type_post') },
    ...(groupContext?.canPostAsGroup ? [{ key: 'action' as const, label: t('post.post_type_action') }] : []),
    { key: 'event', label: t('post.post_type_event') },
    { key: 'bazaar', label: t('post.post_type_offer') },
    { key: 'help', label: t('post.post_type_help') },
  ]

  // Identity picker content — rendered inline in the type-tabs row on
  // desktop, or in its own row below on mobile (see the two render sites).
  const identityPicker = groupContext?.canPostAsGroup ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)' }}>
        {t('groups.post_as_label')}
      </span>
      {([
        { value: false, label: t('groups.post_as_you') },
        { value: true, label: groupContext.name },
      ] as const).map(({ value, label }) => (
        <button
          key={String(value)}
          type="button"
          onClick={() => setPostAsGroup(value)}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-pill)',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 700,
            background: postAsGroup === value ? 'var(--brand)' : 'var(--surface-2)',
            color: postAsGroup === value ? 'var(--on-brand)' : 'var(--text-muted)',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  ) : null

  // Cross-post switch content — same inline-on-desktop / own-row-on-mobile
  // pattern as identityPicker, sharing the language-selector row instead.
  const showCrossPost = !!groupContext || initialPost?.account.accountType === 'group'
  const crossPostSwitch = showCrossPost ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
        {t('groups.cross_post_toggle')}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={crossPost}
        onClick={() => setCrossPost((v) => !v)}
        style={{
          width: 44, height: 26,
          borderRadius: 'var(--radius-pill)',
          border: 'none',
          background: crossPost ? 'var(--brand)' : 'var(--border-strong)',
          cursor: 'pointer',
          position: 'relative',
          flexShrink: 0,
          transition: 'background var(--dur-base) var(--ease-out)',
        }}
      >
        <span style={{
          position: 'absolute',
          top: 3,
          insetInlineStart: crossPost ? 'calc(100% - 23px)' : 3,
          width: 20, height: 20,
          borderRadius: '50%',
          background: '#fff',
          transition: 'inset-inline-start var(--dur-base) var(--ease-out)',
        }} />
      </button>
    </div>
  ) : null

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
              background: 'rgba(8,10,15,0.52)',
              backdropFilter: 'blur(2px)',
            }}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            style={{
              position: 'fixed',
              insetInlineStart: 0,
              insetInlineEnd: 0,
              bottom: 0,
              zIndex: 101,
              background: 'var(--surface)',
              borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              boxShadow: 'var(--shadow-modal)',
              maxHeight: '92dvh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Handle bar */}
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: 'var(--border-strong)',
                margin: '10px auto 0',
                flexShrink: 0,
              }}
            />

            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px 0',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: 'var(--fs-body)',
                  color: 'var(--text-strong)',
                  flex: 1,
                }}
              >
                {initialPost ? t('post.edit_post') : t('post.new_post')}
              </span>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 4,
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Type tabs — only shown for new posts. Desktop: identity picker
                (see below) shares this row, pushed to the far end. */}
            {!initialPost && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '12px 16px 0',
                  flexShrink: 0,
                  justifyContent: isDesktop ? 'space-between' : 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', width: isDesktop ? undefined : '100%' }}>
                  {tabs.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setType(key)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 'var(--radius-pill)',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--fs-sm)',
                        fontWeight: 600,
                        background: type === key ? 'var(--brand)' : 'var(--surface-2)',
                        color: type === key ? 'var(--on-brand)' : 'var(--text-muted)',
                        transition: 'background var(--dur-fast), color var(--dur-fast)',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {isDesktop && identityPicker}
              </div>
            )}

            {/* Posting identity — admin/editor gets a picker (unified (+) entry,
                see SPEC_group_post_creation_unified_entry.md); a regular
                member just sees the static "posting in" banner, no choice.
                Desktop: the picker itself renders inline in the type-tabs
                row above instead of here. */}
            {groupContext && (
              groupContext.canPostAsGroup ? (
                !isDesktop && <div style={{ margin: '10px 16px 0' }}>{identityPicker}</div>
              ) : (
                <div
                  style={{
                    margin: '10px 16px 0',
                    padding: '6px 10px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--brand-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-xs)',
                    fontWeight: 600,
                    color: 'var(--brand)',
                  }}
                >
                  <Users size={14} />
                  {t('groups.posting_in', { name: groupContext.name })}
                </div>
              )
            )}

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px 0' }}>
              {/* Content textarea + mention autocomplete */}
              <div style={{ position: 'relative' }}>
                <textarea
                  ref={textareaRef}
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value)
                    mention.handleChange(e.target.value, e.target.selectionStart)
                  }}
                  onKeyDown={mention.handleKeyDown}
                  placeholder={type === 'help' ? t('help.compose_placeholder') : t('feed.compose_placeholder')}
                  rows={4}
                  style={{
                    width: '100%',
                    minHeight: 100,
                    maxHeight: 200,
                    resize: 'vertical',
                    padding: '10px 12px',
                    borderRadius: 'var(--radius-md)',
                    border: '1.5px solid var(--border)',
                    background: 'var(--bg-sunk)',
                    color: 'var(--text-strong)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-body)',
                    lineHeight: 'var(--lh-normal)',
                    boxSizing: 'border-box',
                    outline: 'none',
                  }}
                />
                {mention.showDropdown && (
                  <MentionDropdown
                    results={mention.results}
                    selectedIdx={mention.selectedIdx}
                    onSelect={mention.selectMention}
                    onClose={mention.closeMention}
                    anchorRef={textareaRef}
                  />
                )}
              </div>
              {submitAttempted && type === 'help' && isContentEmpty && (
                <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--fs-xs)', color: 'var(--danger)', fontFamily: 'var(--font-sans)' }}>
                  {t('common.field_required')}
                </span>
              )}
              {submitAttempted && type !== 'help' && !hasContent && (
                <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--fs-xs)', color: 'var(--danger)', fontFamily: 'var(--font-sans)' }}>
                  {t('post.text_or_image_required')}
                </span>
              )}
              {/* Character counter */}
              <div
                style={{
                  textAlign: 'end',
                  fontSize: 'var(--fs-xs)',
                  fontFamily: 'var(--font-sans)',
                  color: remaining < 20 ? 'var(--danger)' : 'var(--text-faint)',
                  marginTop: 4,
                }}
              >
                {content.length}/{POST_CHAR_LIMIT}
              </div>

              {/* Language selector — desktop: cross-post switch (see below)
                  shares this row, pushed to the far end. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 12,
                  justifyContent: isDesktop ? 'space-between' : 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', gap: 6 }}>
                  {UI_LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setLanguage(lang)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 'var(--radius-pill)',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--fs-xs)',
                        fontWeight: 600,
                        background: language === lang ? 'var(--brand)' : 'var(--surface-2)',
                        color: language === lang ? 'var(--on-brand)' : 'var(--text-muted)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
                {isDesktop && crossPostSwitch}
              </div>

              {/* Cross-post toggle — available whenever the post is tied to a
                  group, regardless of posting identity (self or as-group),
                  or when editing an existing group-authored post. Desktop:
                  renders inline in the language row above instead of here. */}
              {!isDesktop && crossPostSwitch && (
                <div style={{ marginTop: 12 }}>{crossPostSwitch}</div>
              )}

              {/* Help-specific fields */}
              {type === 'help' && (
                <>
                  {/* Category selector (optional) */}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                      {t('help.category_label')}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => setCategoryId(null)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 'var(--radius-pill)',
                          border: '1.5px solid var(--border)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--fs-xs)',
                          fontWeight: 600,
                          background: categoryId === null ? '#E85D3A' : 'var(--surface-2)',
                          color: categoryId === null ? '#fff' : 'var(--text-muted)',
                          transition: 'background var(--dur-fast), color var(--dur-fast)',
                        }}
                      >
                        {t('post.category_none')}
                      </button>
                      {categoryOptions.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => setCategoryId(cat.id)}
                          style={{
                            padding: '4px 10px',
                            borderRadius: 'var(--radius-pill)',
                            border: '1.5px solid var(--border)',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--fs-xs)',
                            fontWeight: 600,
                            background: categoryId === cat.id ? '#E85D3A' : 'var(--surface-2)',
                            color: categoryId === cat.id ? '#fff' : 'var(--text-muted)',
                            transition: 'background var(--dur-fast), color var(--dur-fast)',
                          }}
                        >
                          {cat.translations[categoryLang] ?? cat.translations.en}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Urgent toggle */}
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => setIsUrgent((v) => !v)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: 'var(--radius-pill)',
                        border: isUrgent ? '1.5px solid #DC2626' : '1.5px solid var(--border)',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--fs-xs)',
                        fontWeight: 700,
                        background: isUrgent ? '#DC2626' : 'var(--surface-2)',
                        color: isUrgent ? '#fff' : 'var(--text-muted)',
                        transition: 'background var(--dur-fast)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <LifeBuoy size={12} />
                      {t('help.urgent_label')}
                    </button>
                    {isUrgent && (
                      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: '#DC2626' }}>
                        {t('help.urgent_hint')}
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Image upload — hidden for help posts */}
              {type !== 'help' && (
                <ImageUpload
                  onUploaded={(url) => setImageUrl(url)}
                  onRemoved={() => setImageUrl(null)}
                  currentUrl={imageUrl}
                />
              )}

              {/* Spacer for footer */}
              <div style={{ height: 16 }} />
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid var(--border)',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              {type === 'post' || type === 'help' || initialPost ? (
                <button
                  type="button"
                  onClick={() => { void handleSubmit() }}
                  style={{
                    flex: 1,
                    padding: '11px 0',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: type === 'help' ? '#E85D3A' : 'var(--brand)',
                    color: 'var(--on-brand)',
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 700,
                    fontSize: 'var(--fs-body)',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitValid ? 1 : 0.55,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    transition: 'opacity var(--dur-fast)',
                  }}
                >
                  {isSubmitting && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                  {initialPost ? t('post.save_changes') : type === 'help' ? t('help.publish') : t('post.share')}
                </button>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    type="button"
                    onClick={handleContinue}
                    style={{
                      flex: 1,
                      padding: '11px 0',
                      borderRadius: 'var(--radius-md)',
                      border: 'none',
                      background: 'var(--brand)',
                      color: 'var(--on-brand)',
                      fontFamily: 'var(--font-sans)',
                      fontWeight: 700,
                      fontSize: 'var(--fs-body)',
                      cursor: 'pointer',
                      opacity: isContinueValid ? 1 : 0.55,
                      transition: 'opacity var(--dur-fast)',
                    }}
                  >
                    {t('post.continue_cta')} →
                  </button>
                  <span
                    style={{
                      fontSize: 'var(--fs-xs)',
                      fontFamily: 'var(--font-sans)',
                      color: 'var(--text-faint)',
                    }}
                  >
                    {t('post.continue_hint')}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
